import { Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormValidators } from '@corelabs/angular/forms';
import { ButtonModule } from 'primeng/button';
import { FormCheckboxComponent, FormInputComponent, FormMultiSelectComponent, FormRadioButtonComponent, FormTextareaComponent } from '@shared/forms';
import { MediaImageComponent } from '@shared/components';
import { PackageFacade } from '@app/store/package';
import { PackageAvailabilityType } from '@app/store/shared/models';
import { FormOptionsFacade } from '@app/store/form-options';
import { MediaSelectionService, MediaTarget } from '@app/dialogs';
import { PredefinedPricingTiersFacade } from '@app/store/predefined-pricing-tiers';
import { getAvailabilityOptions } from '../list/config/availability-options';
import { PricingConfiguratorComponent } from '../pricing-configurator/pricing-configurator.component';
import { CustomFormGroup, PricingEntity, createTierFormArray, customGroupToValue, tierGroupToValue } from '../shared';

@Component({
    selector: 'app-package-add-modal',
    templateUrl: './add.component.html',
    styleUrls: ['./add.component.css'],
    imports: [
        ReactiveFormsModule,
        TranslateModule,
        ButtonModule,
        FormInputComponent,
        FormTextareaComponent,
        FormRadioButtonComponent,
        FormMultiSelectComponent,
        FormCheckboxComponent,
        MediaImageComponent,
        PricingConfiguratorComponent,
    ],
})
export class AddComponent implements OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly packageFacade = inject(PackageFacade);
    private readonly formOptionsFacade = inject(FormOptionsFacade);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly mediaSelectionService = inject(MediaSelectionService);
    private readonly translateService = inject(TranslateService);
    private readonly predefinedPricingTiersFacade = inject(PredefinedPricingTiersFacade);

    readonly saving = this.packageFacade.saving;

    readonly availabilityOptions = getAvailabilityOptions(this.translateService);
    readonly courseOptions = computed(() => this.formOptionsFacade.courses());
    readonly pathwayOptions = computed(() => this.formOptionsFacade.predefinedPathways());
    readonly schoolOptions = computed(() => this.formOptionsFacade.schools());
    readonly organisationOptions = computed(() => this.formOptionsFacade.organisations());

    readonly form = this.formBuilder.group({
        name: this.formBuilder.control('', [FormValidators.Required, FormValidators.Text]),
        description: this.formBuilder.control(''),
        thumbnailMediaId: this.formBuilder.control<string | null>(null),
        availabilityType: this.formBuilder.control<PackageAvailabilityType>('general', [FormValidators.Required]),
        courseIds: this.formBuilder.control<string[]>([], [FormValidators.Required]),
        pathwayIds: this.formBuilder.control<string[]>([]),
        assignedSchoolIds: this.formBuilder.control<string[]>([]),
        pricingList: createTierFormArray([]),
        customPricingList: this.formBuilder.array<CustomFormGroup>([]),
        isPurchasable: this.formBuilder.control(false),
    });

    readonly pricingList = signal(this.form.controls.pricingList);

    private readonly availabilityType = toSignal(this.form.controls.availabilityType.valueChanges, {
        initialValue: this.form.controls.availabilityType.value,
    });
    readonly isRestricted = computed(() => this.availabilityType() === 'restricted');
    readonly pricingEntities = computed(() => [
        ...this.schoolOptions().map<PricingEntity>(({ label, value }) => ({ id: value, name: label, type: 'school' })),
        ...this.organisationOptions().map<PricingEntity>(({ label, value }) => ({ id: value, name: label, type: 'organisation' })),
    ]);

    constructor() {
        if (this.formOptionsFacade.coursesLoaded() !== true) {
            this.formOptionsFacade.getCourses();
        }
        if (this.formOptionsFacade.predefinedPathwaysLoaded() !== true) {
            this.formOptionsFacade.getPredefinedPathways();
        }
        if (this.formOptionsFacade.schoolsLoaded() !== true) {
            this.formOptionsFacade.getSchools();
        }
        if (this.formOptionsFacade.organisationsLoaded() !== true) {
            this.formOptionsFacade.getOrganisations();
        }

        if (this.predefinedPricingTiersFacade.loaded() !== true) {
            this.predefinedPricingTiersFacade.getTiers();
        }

        effect(() => {
            if (this.predefinedPricingTiersFacade.loaded() !== true) return;

            const tiers = this.predefinedPricingTiersFacade.tiers();
            if (tiers.length === 0 || this.form.controls.pricingList.length > 0) return;

            this.form.setControl('pricingList', createTierFormArray(tiers));
            this.pricingList.set(this.form.controls.pricingList);
        });

        effect(() => {
            const control = this.form.controls.assignedSchoolIds;
            if (this.isRestricted()) {
                control.setValidators([FormValidators.Required]);
            } else {
                control.clearValidators();
            }
            control.updateValueAndValidity({ emitEvent: false });
        });

        const formEvents = toSignal(this.form.events, { initialValue: null });

        effect(() => {
            formEvents();
            const pricingList = this.form.controls.pricingList;
            const isPurchasable = this.form.controls.isPurchasable;
            const canPurchase = pricingList.length > 0 && pricingList.errors === null;

            if (canPurchase) {
                if (isPurchasable.disabled) {
                    isPurchasable.enable({ emitEvent: false });
                }
            } else {
                if (isPurchasable.value !== false) {
                    isPurchasable.setValue(false, { emitEvent: false });
                }
                if (isPurchasable.enabled) {
                    isPurchasable.disable({ emitEvent: false });
                }
            }
        });

        effect(() => {
            if (this.packageFacade.saved()) {
                this.close();
            }
        });

        effect(() => {
            if (this.packageFacade.saving() === false && this.packageFacade.saved() === false) {
                this.form.enable();
            }
        });

        effect(() => {
            const selection = this.mediaSelectionService.selected();
            if (!selection) return;

            if (selection.target === 'thumbnailMediaId') {
                this.form.controls.thumbnailMediaId.setValue(selection.media.id);
                this.mediaSelectionService.clear();
            }
        });
    }

    ngOnDestroy(): void {
        this.packageFacade.clear();
        this.predefinedPricingTiersFacade.clear();
    }

    onSelectThumbnail(): void {
        const target: MediaTarget = 'thumbnailMediaId';
        this.router.navigate([{ outlets: { dialog: ['select-media', { mediaTarget: target, mediaType: 'image' }] } }]);
    }

    removeThumbnail(): void {
        this.form.controls.thumbnailMediaId.setValue(null);
    }

    onSubmit(): void {
        this.form.markAllAsTouched();

        if (this.form.valid) {
            this.form.disable();
            const value = this.form.getRawValue();
            this.packageFacade.createPackage({
                name: value.name,
                description: value.description,
                thumbnailMediaId: value.thumbnailMediaId,
                originalThumbnailMediaId: null,
                availabilityType: value.availabilityType,
                courseIds: value.courseIds,
                pathwayIds: value.pathwayIds,
                assignedSchoolIds: value.assignedSchoolIds,
                pricingList: this.form.controls.pricingList.controls.map(tierGroupToValue),
                customPricingList: this.form.controls.customPricingList.controls.map(customGroupToValue),
                isPurchasable: value.isPurchasable,
            });
        }
    }

    close(): void {
        this.router.navigate([{ outlets: { modal: null } }], { relativeTo: this.route.parent?.parent });
    }
}
